import type { components } from "@libertify/api-types/media";
import type {
  AddOnType,
  ChatMode,
  PlaybookResponse,
  ProjectAddOnSettingsPatchRequest,
} from "pages/media/types";
import {
  ADDON_DEFAULTS,
  PODCAST_SPEAKER_ROLES,
} from "../components/config-cards/defaults";
import type {
  AddonObject,
  AddonSettings,
} from "../context/PlaybookDraftContext";
import {
  UI_ONLY_ADDON_IDS,
  CHAT_ADDON_ID,
  CONFIG_ITEM,
  type AddonId,
} from "./addons";

/**
 * Glue between the draft and the backend add-ons contract. Add-on ids ARE the
 * backend `AddOnType` values and the card form values ARE the backend settings
 * schemas (see `./addons.ts` and `../components/config-cards/types.ts`), so
 * nothing here translates — it only filters out the UI-only prototypes and
 * picks/copies the settings branches.
 */

const UI_ONLY_IDS: ReadonlySet<string> = new Set(UI_ONLY_ADDON_IDS);

// This and SETTINGS_BRANCHES/nonEmptyBranch below are exported only for
// `mapDraftToPlaybookPayload` in `../helpers`, which must not be imported from
// here in turn — the arrow stays helpers -> data so the two don't form a cycle.
export const isAddOnType = (id: AddonId): id is Extract<AddonId, AddOnType> =>
  !UI_ONLY_IDS.has(id);

export const SETTINGS_BRANCHES = [
  "generation_settings",
  "functional_settings",
] as const;

/** Plain-object narrowing for the draft's untyped settings bags — arrays and
 *  `null` are not records even though `typeof` calls them "object". */
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/** A settings branch worth writing: present, an object, and carrying at least
 *  one field. Same omit-rather-than-send-`{}` rule as `hasFields`, but over
 *  the draft's untyped bag. */
export const nonEmptyBranch = (
  value: unknown
): value is Record<string, unknown> =>
  isRecord(value) && Object.keys(value).length > 0;

/**
 * Merges add-on settings, going one level deep into the API settings branches.
 *
 * antd's `onValuesChange` reports only REGISTERED field paths, so a card's
 * patch can omit a seeded settings field it doesn't render (e.g. a playbook's
 * `voice_profile_id`). A plain shallow spread would let that partial branch
 * replace the seeded one wholesale and silently drop the field; merging the
 * `generation_settings`/`functional_settings` branches one level deeper keeps
 * unrendered fields intact. Non-branch keys (flat general settings) merge
 * shallowly, which is correct for them.
 */
export const mergeSettingsBranches = (
  current: AddonSettings,
  patch: AddonSettings
): AddonSettings => {
  const merged: AddonSettings = { ...current, ...patch };
  for (const branch of SETTINGS_BRANCHES) {
    const currentBranch = current[branch];
    const patchBranch = patch[branch];
    if (isRecord(currentBranch) && isRecord(patchBranch)) {
      merged[branch] = { ...currentBranch, ...patchBranch };
    }
  }
  return merged;
};

/** Org-level default entities an add-on's settings can reference, resolved once
 *  (in `useAddonSeeder`) and injected into the seed so an untouched media
 *  add-on still transmits the org's default brand kit / avatar / voice. */
export interface MediaSeedDefaults {
  brandKitId?: string;
  contactId?: string;
  voiceProfileId?: string;
  /** Distinct voices that can speak a podcast script's emotion tags, in org
   *  order — the pool the speaker roles are seeded from. */
  emotionCapableVoiceProfileIds?: string[];
}

/**
 * Builds the initial draft settings for an add-on: a clone of its card defaults
 * ([[ADDON_DEFAULTS]]), with the org's default media entities injected for the
 * add-ons that reference them (Video → brand kit + avatar; AudioDoc → the
 * default avatar's voice; Podcast → two emotion-capable voices). Cloned one
 * level deep so the shared default constants are never mutated.
 */
export const seedAddonSettings = (
  id: AddonId,
  media: MediaSeedDefaults = {}
): AddonSettings => {
  const base = (ADDON_DEFAULTS[id] ?? {}) as AddonSettings;
  const seed: AddonSettings = { ...base };
  for (const branch of SETTINGS_BRANCHES) {
    const baseBranch = base[branch];
    if (isRecord(baseBranch)) seed[branch] = { ...baseBranch };
  }

  const gen: Record<string, unknown> = isRecord(seed.generation_settings)
    ? seed.generation_settings
    : {};
  // NOTE: the org's default brand kit / avatar used to seed the Video add-on
  // here; they are General settings now — see `seedGeneralSettings`.
  if (id === CONFIG_ITEM.AudioDoc) {
    if (media.voiceProfileId) gen.voice_profile_id = media.voiceProfileId;
    seed.generation_settings = gen;
  } else if (id === CONFIG_ITEM.Podcast) {
    // One emotion-capable voice per speaker role (Host, then Guest), or
    // none at all: any other count is rejected at generation, and seeding the
    // org default avatar's voice (which often can't speak emotions) produced a
    // config that never generated audio. An empty required field the user must
    // fill beats a pre-filled one that silently fails.
    const voices = (media.emotionCapableVoiceProfileIds ?? []).slice(
      0,
      PODCAST_SPEAKER_ROLES.length
    );
    if (voices.length === PODCAST_SPEAKER_ROLES.length) {
      gen.voice_profile_ids = voices;
    }
    seed.generation_settings = gen;
  }
  return seed;
};

/**
 * True when the draft carries a Podcast whose speaker roles aren't both filled.
 *
 * `voice_profile_ids` is positional — one voice per role
 * ([[PODCAST_SPEAKER_ROLES]]) — and the backend takes exactly two, but its
 * schema declares the field optional with no length bounds, so a short array is
 * accepted and only fails later at generation. Gate creation on it instead.
 *
 * False when Podcast isn't in the draft: the rule is the add-on's, not the
 * experience's.
 */
export const hasIncompletePodcastVoices = (draft: AddonObject): boolean => {
  const podcast = draft[CONFIG_ITEM.Podcast];
  if (!podcast) return false;
  const generation = podcast["generation_settings"];
  return hasIncompletePodcastVoiceIds(
    isRecord(generation) ? generation["voice_profile_ids"] : undefined
  );
};

/** True when a podcast's `voice_profile_ids` doesn't fill both speaker roles.
 *  Holes and blanks don't count as a filled role. */
export const hasIncompletePodcastVoiceIds = (voiceIds: unknown): boolean =>
  !Array.isArray(voiceIds) ||
  voiceIds.filter(Boolean).length !== PODCAST_SPEAKER_ROLES.length;

/** Backend add-on types enabled by the draft, in the draft's display order.
 *  Feeds `PATCH /projects/{id}/add-ons/enabled`. */
export const mapDraftToEnabledAddOns = (draft: AddonObject): AddOnType[] =>
  (Object.keys(draft) as AddonId[]).filter(isAddOnType);

/** Instance `chat_mode` implied by the draft, for
 *  `PATCH /projects/{id}/instances/{instanceId}/settings`. The experience page
 *  reads chat from here, not from `enabled_add_ons`. Key presence is the
 *  enabled signal; only `"Disabled"` actually turns chat off. */
export const mapDraftToChatMode = (draft: AddonObject): ChatMode =>
  CHAT_ADDON_ID in draft ? "FullChat" : "Disabled";

export type ProjectAddOnsResponse =
  components["schemas"]["ProjectAddOnsResponse"];

/** False while an add-on's saved settings can't generate it: Podcast
 *  generation fails server-side without both speaker voices (MED-7324). */
export const canShowAddon = (
  id: AddonId,
  settings?: ProjectAddOnsResponse["settings"] | null
): boolean =>
  id !== CONFIG_ITEM.Podcast ||
  !hasIncompletePodcastVoiceIds(
    settings?.podcast_generation_settings.voice_profile_ids
  );

/**
 * Order the add-ons appear in the edit rail. A `Record` rather than a list so a
 * new backend `AddOnType` fails to compile until it's placed here, instead of
 * silently never showing up in the rail. `"General"` is excluded (see
 * `AddonId`): the GET always returns its settings branches, so listing it here
 * would duplicate the fixed general settings entry as an add-on row.
 */
const ADD_ON_RAIL_ORDER: Record<Exclude<AddOnType, "General">, number> = {
  Video: 0,
  AudioDoc: 1,
  Podcast: 2,
  Summary: 3,
  Quiz: 4,
  Flashcards: 5,
  Bookmarks: 6,
  Trivia: 7,
  Standouts: 8,
  Chatbot: 9,
};

/**
 * The add-ons that exist on a project: the ones `GET /projects/{id}/add-ons`
 * returns a settings branch for. Existing is distinct from enabled — an add-on
 * that exists can be hidden from viewers (absent from `enabled_add_ons`) while
 * still being configured, which is what keeps it listed in the edit rail across
 * reloads.
 *
 * Settings keys follow the same snake_case convention as the playbook ones, so
 * the key derivation is shared with `playbookSettingsKey` (and its test).
 */
export const readConfiguredAddOns = (
  settings?: ProjectAddOnsResponse["settings"] | null
): Extract<AddonId, AddOnType>[] => {
  if (!settings) return [];
  const branches = settings as Record<string, unknown>;
  return (Object.keys(ADD_ON_RAIL_ORDER) as Extract<AddonId, AddOnType>[])
    .sort((a, b) => ADD_ON_RAIL_ORDER[a] - ADD_ON_RAIL_ORDER[b])
    .filter(
      (type) =>
        branches[playbookSettingsKey(type, "generation")] != null ||
        branches[playbookSettingsKey(type, "functional")] != null
    );
};

/** An empty settings object must be OMITTED from the payload, not sent as
 *  `{}`: the backend resolves `generation_settings`/`functional_settings`
 *  through a union of every add-on's settings model, and `{}` is ambiguous —
 *  it matches all of them with zero fields set, pydantic picks the first
 *  (Quiz), and the endpoint's add_on_type cross-check rejects the request
 *  with a 422 ("functional_settings must be SummaryFunctionalSettings for
 *  add_on type Summary"). Omitting means the same thing (leave untouched). */
const hasFields = (settings: object | null | undefined): settings is object =>
  settings != null && Object.keys(settings).length > 0;

/** One `PATCH /projects/{id}/add-ons/settings` payload per backend add-on in
 *  the draft. Picks only the API settings branches — prototype-only form
 *  fields stay behind. Add-ons whose draft carries no settings (no config card
 *  yet, or never seeded) produce no payload: backend/playbook defaults
 *  apply. */
export const mapDraftToSettingsPayloads = (
  draft: AddonObject
): ProjectAddOnSettingsPatchRequest[] => {
  const payloads: ProjectAddOnSettingsPatchRequest[] = [];
  for (const id of Object.keys(draft) as AddonId[]) {
    if (!isAddOnType(id)) continue;
    const stored = draft[id] ?? {};
    const generation = stored[
      "generation_settings"
    ] as ProjectAddOnSettingsPatchRequest["generation_settings"];
    const functional = stored[
      "functional_settings"
    ] as ProjectAddOnSettingsPatchRequest["functional_settings"];
    if (!hasFields(generation) && !hasFields(functional)) continue;
    payloads.push({
      add_on_type: id,
      ...(hasFields(generation) && { generation_settings: generation }),
      ...(hasFields(functional) && { functional_settings: functional }),
    });
  }
  return payloads;
};

/** `PlaybookResponse` settings keys follow a strict snake_case convention
 *  (`AudioDoc` → `audio_doc_generation_settings`) — pinned by the unit test so
 *  a backend deviation fails loudly instead of silently seeding nothing. */
export const playbookSettingsKey = (
  addOnType: AddOnType,
  kind: "generation" | "functional"
): keyof PlaybookResponse =>
  `${addOnType
    .replace(/([a-z])([A-Z])/g, "$1_$2")
    .toLowerCase()}_${kind}_settings` as keyof PlaybookResponse;

/** Seeds a draft from a playbook: the playbook's enabled add-ons in backend
 *  order. The playbook's settings win, with the card's static defaults filling
 *  any field it doesn't specify (so an untouched card still shows/sends a
 *  coherent value). */
export const mapPlaybookToDraft = (playbook: PlaybookResponse): AddonObject => {
  const draft: AddonObject = {};
  for (const addOnType of playbook.enabled_add_ons?.enabled_add_ons ?? []) {
    // "General" is not a rail add-on (see `AddonId`) — its settings belong to
    // the general settings card, not an add-on draft slot.
    if (addOnType === "General") continue;
    const generation = playbook[playbookSettingsKey(addOnType, "generation")];
    const functional = playbook[playbookSettingsKey(addOnType, "functional")];
    const playbookSettings: AddonSettings = {
      ...(generation != null && { generation_settings: generation }),
      ...(functional != null && { functional_settings: functional }),
    };
    draft[addOnType] = mergeSettingsBranches(
      seedAddonSettings(addOnType),
      playbookSettings
    );
  }
  return draft;
};

export const playbookHasBrandKit = (playbook: PlaybookResponse): boolean =>
  !!playbook.general_generation_settings?.brand_kit_id;

/**
 * The general-settings counterpart of `mapPlaybookToDraft`: the playbook's
 * `general_*_settings` as the general card's settings branches, to seed the
 * draft's general slot on selection. Without this, customizing a playbook
 * would rebuild the derived playbook's General branches from card defaults,
 * silently dropping the source playbook's language/brand kit/side panel.
 */
export const mapPlaybookToGeneralSettings = (
  playbook: PlaybookResponse
): AddonSettings => ({
  ...(playbook.general_generation_settings != null && {
    generation_settings: playbook.general_generation_settings,
  }),
  ...(playbook.general_functional_settings != null && {
    functional_settings: playbook.general_functional_settings,
  }),
});

/**
 * Background seed for the General settings branches: the org's default brand
 * kit / avatar, sent with the experience-wide settings even when the user
 * never opens the general card. Same role `seedAddonSettings` played for the
 * Video add-on while these were Video fields. The card's own fields (language,
 * side panel) come from the draft and are merged OVER this seed.
 */
export const seedGeneralSettings = (
  media: MediaSeedDefaults = {}
): AddonSettings => ({
  generation_settings: {
    ...(media.brandKitId && { brand_kit_id: media.brandKitId }),
    ...(media.contactId && { contact_id: media.contactId }),
  },
});
